import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {NavigateFunction} from 'react-router-dom';
import {RootState} from '@store/index';
import {api} from '../api';
import {helperHandleError} from '../helpers';
import {IError, ISuccess} from '../helpers/types';
import {ILoginProps, IRegistrationProps, IStateApp, ITokenRes, IUpdatePasswordProps} from './types';

const initialState: IStateApp = {
    loading: false,
    errorReq: null,
    successReq: null,
    resetSuccess: false,
    token: null,
    expires_at: null,
    brands: [],
    carBrands: [],
    carBrandsTotal: 0,
    brandsTotal: 0,
    order_by: {
        key: 'bulk_price',
        direction: 'desc'
    }
};

export const registration = createAsyncThunk(
    'app/registration',
    async ({login, password, navigate}: IRegistrationProps, {dispatch}) => {
        try {
            const res = await api.post<ITokenRes>('/auth/register', {login, password});
            if (res.status === 200) {
                localStorage.setItem('token', JSON.stringify(res.data.token));
                dispatch(setToken({token: res.data.token, expires_at: res.data.expires_at}));
                navigate('/profile');
            }
            return;
        } catch (e: any) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const login = createAsyncThunk(
    'app/login',
    async ({login, password, navigateAfterLogin, remember_me}: ILoginProps, {dispatch}) => {
        try {
            const res = await api.post<ITokenRes>('/auth/login', {login, password, remember_me});
            if (res.status === 200) {
                localStorage.setItem('token', JSON.stringify(res.data.token));
                dispatch(setToken({token: res.data.token, expires_at: res.data.expires_at}));
                if (navigateAfterLogin) {
                    // @ts-ignore
                    window.location = navigateAfterLogin;
                } else {
                    window.location.reload();
                }
            }
            return;
        } catch (e) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const resetPassword = createAsyncThunk(
    'app/resetPassword',
    async ({login}: { login: string }, {dispatch, getState}) => {
        try {
            const res = await api.post<{ message: string, close_page: boolean }>('auth/reset-password/reset', {login});
            const {close_page} = res.data;
            if (!close_page) {
                dispatch(handleResetPassword(true));
            }
            return res.data;
        } catch (e: any) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const updatePassword = createAsyncThunk(
    'app/updatePassword',
    async ({token, password, login, navigate}: IUpdatePasswordProps, {dispatch}) => {
        try {
            const res = await api.post<ITokenRes>(`/auth/reset-password/update/${token}`, {login, password});
            if (res.status === 200) {
                dispatch(setToken({token: res.data.token, expires_at: res.data.expires_at}));
                navigate('/profile');
            }
            return;
        } catch (e: any) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

export const logout = createAsyncThunk(
    'app/logout',
    async ({navigate}: { navigate: NavigateFunction }, {getState, dispatch}) => {
        const {app} = getState() as { app: IStateApp };
        try {
            const res = await api.post<{ message: string }>('/auth/logout', null, {
                headers: {
                    'Authorization': `${app?.token?.token_type} ${app?.token?.token}`
                }
            }).catch(async e => {
                await localStorage.removeItem('token');
                dispatch(setToken({token: null, expires_at: null}));
                navigate('/login');
                throw e;
            });
            return;
        } catch (e) {
            helperHandleError('registration, appSlice', e, dispatch);
        }
    }
);

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        handleError: (state, {payload}: { payload: IError | null }) => {
            state.errorReq = payload;
        },
        handleSuccess: (state, {payload}: { payload: ISuccess | null }) => {
            state.successReq = payload;
        },
        handleResetPassword: (state, {payload}: { payload: boolean }) => {
            state.resetSuccess = payload;
        },
        setToken: (state, {payload}) => {
            state.token = payload.token;
            state.expires_at = payload.token?.expires_at ?? null;
        },
    },
});

export const {handleError, handleSuccess, handleResetPassword, setToken} = appSlice.actions;
export const selectToken = (state: RootState) => state.app?.token || {token: '', token_type: ''};

export default appSlice.reducer;
